import React, { Component } from 'react';
import Axios from 'axios';
import moment from 'moment';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import MultiSelect from '@khanacademy/react-multi-select';
import { getUser } from '../../actions/session';
import { ALL_CARRIER_LIST, CARRIER_MEDIA_STATS_API, MEDIA_IP_BLOCK_LISTING } from '../../config/rest_endpoints';
import Container from '../common/application_container';
import CustomLoader from '../common/loader';
import { Notifications } from '../common/notification';
import { fmtInt, fmtMins } from '../common/formatters';

const KPI_CARD = {
    background: '#FFFFFF',
    border: '1px solid #E2E8F0',
    borderRadius: '8px',
    padding: '14px 16px',
    minWidth: '160px',
    flex: '1 1 160px',
};

class CarrierMediaStatsReport extends Component {
    constructor(props) {
        super(props);
        this.state = {
            User: getUser(),
            data: [],
            carrierList: [],
            selectedCarrier: [],
            startDate: moment().utc().startOf('day'),
            endDate: moment().utc(),
            quickDatePreset: 'today',
            mediaIP: '',
            sortColumn: 'fasRisk',
            sortDirection: 'desc',
            isGetting: false,
            isBtnSpin: false,
            isDownLoadSpin: false,
            blockedMediaIPs: {},
            blockingMediaIP: '',
        };
        this.fetchReport = this.fetchReport.bind(this);
        this.fetchCarriers = this.fetchCarriers.bind(this);
        this.handleDate = this.handleDate.bind(this);
        this.handleQuickDateSelect = this.handleQuickDateSelect.bind(this);
        this.handleSort = this.handleSort.bind(this);
        this.handleMediaIP = this.handleMediaIP.bind(this);
        this.handleMediaIPKeyDown = this.handleMediaIPKeyDown.bind(this);
        this.clearMediaIP = this.clearMediaIP.bind(this);
        this.fetchBlockedMediaIPs = this.fetchBlockedMediaIPs.bind(this);
        this.blockMediaIP = this.blockMediaIP.bind(this);
        this.unblockMediaIP = this.unblockMediaIP.bind(this);
    }

    componentDidMount() {
        this.fetchCarriers();
        this.fetchBlockedMediaIPs();
        this.fetchReport();
    }

    fetchBlockedMediaIPs() {
        const { User } = this.state;
        const headers = { Authorization: 'Bearer ' + User.token };
        Axios.get(MEDIA_IP_BLOCK_LISTING, { headers, params: { page: 1, limit: 10000 } }).then(res => {
            const blockedMediaIPs = (res.data.data || []).reduce((result, item) => {
                if (item.ip) result[item.ip] = item;
                return result;
            }, {});
            this.setState({ blockedMediaIPs });
        }).catch(err => {
            const msg = err.response && err.response.data ? err.response.data.message : 'Unable to load blocked media IPs';
            Notifications.notify(msg, 'Failed', 'error');
        });
    }

    blockMediaIP(row) {
        const mediaIP = row.mediaIp;
        if (!mediaIP || this.state.blockedMediaIPs[mediaIP] || this.state.blockingMediaIP) return;
        const blockDetails = [
            `Media IP: ${mediaIP}`,
            `Carrier: ${row.carrierName || row.carrierID || 'Unknown'}`,
            `Owner: ${row.mediaOwner || 'Unknown'}`,
            `Network: ${row.mediaNetName || row.mediaCidr || 'Unknown'}`,
            `Country: ${row.mediaCountry || 'Unknown'}`,
            `Attempts: ${fmtInt(row.attemptsCarrier || 0)}`,
        ].join('\n');
        if (!window.confirm(`Confirm media IP block\n\n${blockDetails}\n\nThis IP will be added to the global media block list.`)) return;

        const { User } = this.state;
        const headers = { Authorization: 'Bearer ' + User.token };
        const note = `Blocked from Carrier Media Stats (${row.carrierName || row.carrierID || 'unknown carrier'})`;
        this.setState({ blockingMediaIP: mediaIP });
        Axios.post(MEDIA_IP_BLOCK_LISTING, { ip: mediaIP, note }, { headers }).then(res => {
            this.setState(state => ({
                blockedMediaIPs: { ...state.blockedMediaIPs, [mediaIP]: res.data.data || { ip: mediaIP } },
                blockingMediaIP: '',
            }));
            Notifications.notify(res.data.message || `Media IP ${mediaIP} blocked`, 'Success', 'success');
        }).catch(err => {
            const msg = err.response && err.response.data ? err.response.data.message : 'Unable to block media IP';
            this.setState({ blockingMediaIP: '' });
            if (msg.toLowerCase().indexOf('already exists') >= 0) this.fetchBlockedMediaIPs();
            Notifications.notify(msg, 'Failed', 'error');
        });
    }

    unblockMediaIP(row) {
        const mediaIP = row.mediaIp;
        const blockEntry = this.state.blockedMediaIPs[mediaIP];
        if (!blockEntry || !blockEntry.id || this.state.blockingMediaIP) return;
        if (!window.confirm(`Unblock media IP ${mediaIP}? It will be removed from the global media block list.`)) return;

        const { User } = this.state;
        const headers = { Authorization: 'Bearer ' + User.token };
        this.setState({ blockingMediaIP: mediaIP });
        Axios.delete(`${MEDIA_IP_BLOCK_LISTING}/${blockEntry.id}`, { headers }).then(res => {
            this.setState(state => {
                const blockedMediaIPs = { ...state.blockedMediaIPs };
                delete blockedMediaIPs[mediaIP];
                return { blockedMediaIPs, blockingMediaIP: '' };
            });
            Notifications.notify(res.data.message || `Media IP ${mediaIP} unblocked`, 'Success', 'success');
        }).catch(err => {
            const msg = err.response && err.response.data ? err.response.data.message : 'Unable to unblock media IP';
            this.setState({ blockingMediaIP: '' });
            Notifications.notify(msg, 'Failed', 'error');
        });
    }

    fetchCarriers() {
        const { User } = this.state;
        const headers = { Authorization: 'Bearer ' + User.token };
        Axios.get(ALL_CARRIER_LIST, { headers }).then(res => {
            const list = (res.data.data || []).map(row => ({
                ...row,
                label: row.name,
                value: row.domain,
            }));
            this.setState({ carrierList: list });
        }).catch(err => {
            const msg = err.response && err.response.data ? err.response.data.message : 'Unable to load carriers';
            Notifications.notify(msg, 'Failed', 'error');
        });
    }

    handleDate(date, value) {
        if (value === 'start') {
            this.setState({ startDate: date, quickDatePreset: '' });
        } else {
            this.setState({ endDate: date, quickDatePreset: '' });
        }
    }

    handleQuickDateSelect(preset) {
        let startDate, endDate;
        switch (preset) {
            case 'today':
                startDate = moment().utc().startOf('day');
                endDate = moment().utc();
                break;
            case 'yesterday':
                startDate = moment().utc().subtract(1, 'day').startOf('day');
                endDate = moment().utc().subtract(1, 'day').endOf('day');
                break;
            case 'thisWeek':
                startDate = moment().utc().startOf('isoWeek');
                endDate = moment().utc();
                break;
            case 'thisMonth':
                startDate = moment().utc().startOf('month');
                endDate = moment().utc();
                break;
            default:
                return;
        }
        this.setState({ startDate, endDate, quickDatePreset: preset });
    }

    handleMediaIP(event) {
        this.setState({ mediaIP: event.target.value });
    }

    handleMediaIPKeyDown(event) {
        if (event.key === 'Enter') {
            this.fetchReport();
        }
    }

    clearMediaIP() {
        this.setState({ mediaIP: '' }, () => this.fetchReport());
    }

    handleSort(column) {
        const { sortColumn, sortDirection } = this.state;
        let nextDirection = 'desc';
        if (sortColumn === column && sortDirection === 'desc') {
            nextDirection = 'asc';
        }
        this.setState({ sortColumn: column, sortDirection: nextDirection });
    }

    buildPayload() {
        const { startDate, endDate, selectedCarrier, mediaIP } = this.state;
        return {
            startDate: moment(startDate).format('MM/DD/YYYY, h:mm:ss A'),
            endDate: moment(endDate).format('MM/DD/YYYY, h:mm:ss A'),
            accounts: selectedCarrier,
            destination: mediaIP.trim(),
        };
    }

    fetchReport(mode) {
        const { User } = this.state;
        const payload = this.buildPayload();
        const headers = { Authorization: 'Bearer ' + User.token };

        if (mode === 'download') {
            this.setState({ isDownLoadSpin: true });
            Axios.post(`${CARRIER_MEDIA_STATS_API}?download=true`, payload, { headers }).then(res => {
                const fileNameHeader = res.headers['content-disposition'] || 'attachment; filename=carrierMediaStats.csv';
                const fileName = fileNameHeader.replace('attachment; filename=', '');
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                this.setState({ isDownLoadSpin: false });
            }).catch(err => {
                const msg = err.response && err.response.data ? err.response.data.message : 'Download failed';
                Notifications.notify(msg, 'Failed', 'error');
                this.setState({ isDownLoadSpin: false });
            });
            return;
        }

        this.setState({ isGetting: true, isBtnSpin: true });
        Axios.post(CARRIER_MEDIA_STATS_API, payload, { headers }).then(res => {
            this.setState({
                data: res.data.data || [],
                isGetting: false,
                isBtnSpin: false,
            });
        }).catch(err => {
            const msg = err.response && err.response.data ? err.response.data.message : 'Carrier media stats failed';
            Notifications.notify(msg, 'Failed', 'error');
            this.setState({ isGetting: false, isBtnSpin: false });
        });
    }

    getSortedRows() {
        const { data, sortColumn, sortDirection } = this.state;
        return data.slice().sort((a, b) => {
            if (sortColumn === 'fasRisk') {
                const riskA = this.fasRiskScore(a);
                const riskB = this.fasRiskScore(b);
                if (riskA === riskB) {
                    return (b.attemptsCarrier || 0) - (a.attemptsCarrier || 0);
                }
                return sortDirection === 'asc' ? riskA - riskB : riskB - riskA;
            }

            let valA = a[sortColumn];
            let valB = b[sortColumn];
            if (sortColumn === 'carrierName' || sortColumn === 'mediaIp') {
                valA = (valA || '').toLowerCase();
                valB = (valB || '').toLowerCase();
                if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
                if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
                return 0;
            }
            valA = Number(valA || 0);
            valB = Number(valB || 0);
            return sortDirection === 'asc' ? valA - valB : valB - valA;
        });
    }

    isPrivateMediaRow(row) {
        return row.mediaIssueCategory === 'private' || ((row.mediaIpScope || '').toLowerCase() !== 'public');
    }

    getFasRows(rows) {
        return rows.filter(row => !this.isPrivateMediaRow(row));
    }

    getPrivateRows(rows) {
        return rows.filter(row => this.isPrivateMediaRow(row));
    }

    fasRiskScore(row) {
        if (row.fasRisk !== undefined && row.fasRisk !== null) {
            return Number(row.fasRisk || 0);
        }
        const attempts = Number(row.attemptsCarrier || 0);
        const completed = Number(row.completed || 0);
        const asr = Number(row.ASR || 0);
        const acd = Number(row.ACD || 0);

        if (attempts <= 0) return 0;

        const volumeWeight = Math.min(35, Math.log10(attempts + 1) * 10);
        const lowAsrRisk = Math.max(0, 45 - asr);
        const noAnswerRisk = completed === 0 && attempts >= 10 ? 20 : 0;
        const lowAcdRisk = completed >= 5 ? Math.max(0, 12 - acd) * 2.5 : 0;

        return Math.round((lowAsrRisk + noAnswerRisk + lowAcdRisk + volumeWeight) * 100) / 100;
    }

    fasReason(row) {
        if (row.mediaIssue) return row.mediaIssue;
        const attempts = Number(row.attemptsCarrier || 0);
        const completed = Number(row.completed || 0);
        const asr = Number(row.ASR || 0);
        const acd = Number(row.ACD || 0);
        if (attempts >= 10 && completed === 0) return 'No answers';
        if (attempts >= 10 && asr < 25 && completed >= 5 && acd < 8) return 'Low ASR + short ACD';
        if (attempts >= 10 && asr < 25) return 'Low ASR';
        if (completed >= 5 && acd < 8) return 'Short ACD';
        return 'No issue signal';
    }

    getFasSuspectRows() {
        return this.state.data.filter(row => {
            if (row.mediaIssue) return true;
            const attempts = Number(row.attemptsCarrier || 0);
            const completed = Number(row.completed || 0);
            const asr = Number(row.ASR || 0);
            const acd = Number(row.ACD || 0);
            return attempts >= 10 && (completed === 0 || asr < 25 || (completed >= 5 && acd < 8));
        });
    }

    getSummary() {
        const { data } = this.state;
        const summary = data.reduce((acc, row) => {
            acc.attempts += row.attemptsCarrier || 0;
            acc.completed += row.completed || 0;
            acc.duration += row.carrierCallDurationRaw || 0;
            acc.minutes += row.minutes || 0;
            acc.media[row.mediaIp] = true;
            if (this.isPrivateMediaRow(row)) {
                acc.privateMedia[row.mediaIp] = true;
            }
            if (row.mediaIssue === 'Low ASR' || row.mediaIssue === 'Low ASR + short ACD' || this.fasReason(row) === 'Low ASR') {
                acc.lowAsr += 1;
            }
            if (row.mediaIssue === 'Short ACD' || row.mediaIssue === 'Low ASR + short ACD' || this.fasReason(row) === 'Short ACD') {
                acc.shortAcd += 1;
            }
            return acc;
        }, { attempts: 0, completed: 0, duration: 0, minutes: 0, media: {}, privateMedia: {}, lowAsr: 0, shortAcd: 0 });
        summary.asr = summary.attempts > 0 ? (summary.completed / summary.attempts * 100) : 0;
        summary.acd = summary.completed > 0 ? (summary.duration / summary.completed) : 0;
        summary.mediaCount = Object.keys(summary.media).length;
        summary.privateMediaCount = Object.keys(summary.privateMedia).length;
        return summary;
    }

    getMediaIPCarrierSummary(rows) {
        return rows.reduce((acc, row) => {
            const carrierKey = row.carrierID || row.carrierName || 'unknown';
            if (!acc[carrierKey]) {
                acc[carrierKey] = {
                    carrierID: row.carrierID,
                    carrierName: row.carrierName || row.carrierID,
                    attempts: 0,
                    completed: 0,
                    duration: 0,
                    minutes: 0,
                    media: {},
                };
            }
            acc[carrierKey].attempts += row.attemptsCarrier || 0;
            acc[carrierKey].completed += row.completed || 0;
            acc[carrierKey].duration += row.carrierCallDurationRaw || 0;
            acc[carrierKey].minutes += row.minutes || 0;
            acc[carrierKey].media[row.mediaIp] = true;
            return acc;
        }, {});
    }

    renderSortIcon(column) {
        const { sortColumn, sortDirection } = this.state;
        if (sortColumn !== column) {
            return <i className="icon-sort ml-1" style={{ fontSize: '10px', opacity: 0.3 }}></i>;
        }
        return sortDirection === 'asc'
            ? <i className="icon-sort-amount-asc ml-1" style={{ fontSize: '10px' }}></i>
            : <i className="icon-sort-amount-desc ml-1" style={{ fontSize: '10px' }}></i>;
    }

    renderKpi(label, value, tone, sub) {
        return (
            <div style={KPI_CARD}>
                <div style={{ color: '#64748B', fontSize: '11px', textTransform: 'uppercase', fontWeight: '700', marginBottom: '6px' }}>{label}</div>
                <div style={{ color: tone || '#0F172A', fontSize: '24px', fontWeight: '700', lineHeight: '28px' }}>{value}</div>
                <div style={{ color: '#94A3B8', fontSize: '11px', marginTop: '4px' }}>{sub}</div>
            </div>
        );
    }

    render() {
        const { startDate, endDate, quickDatePreset, carrierList, selectedCarrier, mediaIP, isGetting, isBtnSpin, isDownLoadSpin, blockedMediaIPs, blockingMediaIP } = this.state;
        const rows = this.getSortedRows();
        const searchedMediaIP = mediaIP.trim();
        const fasRows = searchedMediaIP ? rows : this.getFasRows(rows);
        const privateRows = searchedMediaIP ? [] : this.getPrivateRows(rows);
        const summary = this.getSummary();
        const mediaIPCarrierSummary = Object.values(this.getMediaIPCarrierSummary(rows))
            .sort((a, b) => b.attempts - a.attempts);
        const presetButtons = [
            { key: 'today', label: 'Today' },
            { key: 'yesterday', label: 'Yesterday' },
            { key: 'thisWeek', label: 'This Week' },
            { key: 'thisMonth', label: 'This Month' },
        ];
        const th = { fontSize: '12px', fontWeight: '700', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' };
        const thRight = { ...th, textAlign: 'right' };
        const td = { fontSize: '12px', verticalAlign: 'middle' };
        const tdRight = { ...td, textAlign: 'right' };

        return (
            <Container title="Carrier Media" title1="Issue Watch" icon="fa fa-sitemap">
                <div style={{ border: '1px solid #E2E8F0', borderRadius: '12px', background: '#FFFFFF', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', overflow: 'hidden' }}>
                    <div style={{ padding: '18px' }}>
                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '14px' }}>
                            {presetButtons.map(p => (
                                <button key={p.key} className={`btn btn-sm ${quickDatePreset === p.key ? 'btn-info' : 'btn-outline-secondary'}`} onClick={() => this.handleQuickDateSelect(p.key)} style={{ fontSize: '12px' }}>
                                    {p.label}
                                </button>
                            ))}
                            <DateRangePicker onApply={(e, picker) => this.handleDate(picker.startDate, 'start')} initialSettings={{ singleDatePicker: true, startDate: moment(startDate) }}>
                                <input type="text" className="form-control form-control-sm" style={{ width: '140px', fontSize: '12px' }} value={moment(startDate).format('MM/DD/YYYY')} readOnly />
                            </DateRangePicker>
                            <span style={{ color: '#94A3B8', fontSize: '12px' }}>to</span>
                            <DateRangePicker onApply={(e, picker) => this.handleDate(picker.startDate, 'end')} initialSettings={{ singleDatePicker: true, startDate: moment(endDate) }}>
                                <input type="text" className="form-control form-control-sm" style={{ width: '140px', fontSize: '12px' }} value={moment(endDate).format('MM/DD/YYYY')} readOnly />
                            </DateRangePicker>
                            <div style={{ display: 'flex', alignItems: 'center', width: '250px' }}>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Search media IP to find carriers"
                                    value={mediaIP}
                                    onChange={this.handleMediaIP}
                                    onKeyDown={this.handleMediaIPKeyDown}
                                    style={{ fontSize: '12px', borderTopRightRadius: mediaIP ? 0 : undefined, borderBottomRightRadius: mediaIP ? 0 : undefined }}
                                />
                                {mediaIP ? (
                                    <button className="btn btn-outline-secondary btn-sm" onClick={this.clearMediaIP} style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}>
                                        <i className="icon-cross2"></i>
                                    </button>
                                ) : null}
                            </div>
                            <button className="btn btn-primary btn-sm" onClick={() => this.fetchReport()} style={{ fontWeight: '600' }}>
                                Search <i className={`${isBtnSpin ? 'icon-spinner4 spinner' : 'icon-arrow-right8'} ml-2`}></i>
                            </button>
                            <button className="btn btn-outline-secondary btn-sm" disabled={isDownLoadSpin} onClick={() => this.fetchReport('download')} style={{ fontWeight: '600' }}>
                                Download CSV <i className={`${isDownLoadSpin ? 'icon-spinner4 spinner' : 'icon-download4'} ml-2`}></i>
                            </button>
                        </div>

                        <div style={{ maxWidth: '460px', marginBottom: '16px' }}>
                            <MultiSelect
                                options={carrierList}
                                selected={selectedCarrier}
                                onSelectedChanged={selected => this.setState({ selectedCarrier: selected })}
                                overrideStrings={{ selectSomeItems: 'Filter carriers', allItemsAreSelected: 'All carriers selected', selectAll: 'Select all carriers', search: 'Search carriers' }}
                            />
                        </div>

                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px' }}>
                            {this.renderKpi('FAS Suspects', fmtInt(fasRows.length), '#DC2626', searchedMediaIP ? 'Matching media IP rows' : 'Volume + low ASR/short ACD')}
                            {this.renderKpi('Media IPs', fmtInt(summary.mediaCount), '#7C3AED', 'Unique problem media IPs')}
                            {this.renderKpi('Private IP Rows', fmtInt(privateRows.length), '#B91C1C', 'Separate media config issue')}
                            {this.renderKpi('Low ASR Rows', fmtInt(summary.lowAsr), '#EA580C', 'Poor answer ratio')}
                            {this.renderKpi('Short ACD Rows', fmtInt(summary.shortAcd), '#D97706', 'Answered but too short')}
                            {this.renderKpi('Attempts', fmtInt(summary.attempts), '#2563EB', `${fmtMins(summary.minutes)} minutes`)}
                        </div>
                        {!searchedMediaIP ? (
                            <div style={{ color: '#64748B', fontSize: '12px', marginTop: '-8px', marginBottom: '14px' }}>
                                Main table shows FAS suspects only, prioritized by call volume plus low ASR, no answers, and short ACD. Private/non-public media IPs are shown separately below.
                            </div>
                        ) : null}

                        {searchedMediaIP && !isGetting ? (
                            <div style={{ border: '1px solid #CBD5E1', background: '#F8FAFC', borderRadius: '8px', padding: '12px 14px', marginBottom: '16px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', marginBottom: mediaIPCarrierSummary.length > 0 ? '10px' : 0 }}>
                                    <div>
                                        <div style={{ color: '#0F172A', fontWeight: '700', fontSize: '13px' }}>Media IP Search</div>
                                        <div style={{ color: '#64748B', fontSize: '12px' }}>
                                            <code style={{ color: '#334155', background: '#E2E8F0', padding: '2px 6px', borderRadius: '4px' }}>{searchedMediaIP}</code>
                                            <span style={{ marginLeft: '8px' }}>{fmtInt(mediaIPCarrierSummary.length)} carrier{mediaIPCarrierSummary.length === 1 ? '' : 's'} found in selected date range</span>
                                        </div>
                                    </div>
                                </div>
                                {mediaIPCarrierSummary.length > 0 ? (
                                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                        {mediaIPCarrierSummary.map(carrier => (
                                            <div key={carrier.carrierID || carrier.carrierName} style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '6px', padding: '8px 10px', minWidth: '190px' }}>
                                                <div style={{ color: '#0F172A', fontWeight: '700', fontSize: '12px', marginBottom: '3px' }}>{carrier.carrierName}</div>
                                                <div style={{ color: '#64748B', fontSize: '11px' }}>
                                                    {fmtInt(carrier.attempts)} attempts · {fmtInt(carrier.completed)} completed · {fmtMins(carrier.minutes)} minutes
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : null}
                            </div>
                        ) : null}

                        {isGetting ? <CustomLoader /> : null}

                        {!isGetting && fasRows.length === 0 && privateRows.length === 0 ? (
                            <div style={{ border: '1px solid #E2E8F0', borderRadius: '8px', padding: '18px', textAlign: 'center', color: '#94A3B8', fontSize: '13px' }}>
                                No carrier media stats found for the selected filters.
                            </div>
                        ) : null}

                        {fasRows.length > 0 && (
                            <div className="table-responsive" style={{ maxHeight: '520px', overflowY: 'auto', overflowX: 'auto', width: '100%', WebkitOverflowScrolling: 'touch' }}>
                                {!searchedMediaIP ? (
                                    <div style={{ color: '#0F172A', fontWeight: '700', fontSize: '14px', marginBottom: '8px' }}>FAS Suspects</div>
                                ) : null}
                                <table className="table table-striped table-sm" style={{ minWidth: '1250px' }}>
                                    <thead>
                                        <tr>
                                            <th style={th} onClick={() => this.handleSort('carrierName')}>Carrier Name {this.renderSortIcon('carrierName')}</th>
                                            <th style={th} onClick={() => this.handleSort('mediaIp')}>Media IP {this.renderSortIcon('mediaIp')}</th>
                                            <th style={th}>Owner</th>
                                            <th style={thRight} onClick={() => this.handleSort('attemptsCarrier')}>Attempts {this.renderSortIcon('attemptsCarrier')}</th>
                                            <th style={thRight} onClick={() => this.handleSort('completed')}>Completed {this.renderSortIcon('completed')}</th>
                                            <th style={thRight} onClick={() => this.handleSort('ASR')}>ASR {this.renderSortIcon('ASR')}</th>
                                            <th style={thRight} onClick={() => this.handleSort('ACD')}>ACD {this.renderSortIcon('ACD')}</th>
                                            <th style={thRight} onClick={() => this.handleSort('fasRisk')}>Issue Score {this.renderSortIcon('fasRisk')}</th>
                                            <th style={th}>Signal</th>
                                            <th style={thRight} onClick={() => this.handleSort('minutes')}>Minutes {this.renderSortIcon('minutes')}</th>
                                            <th style={th}>Currently Blocked</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {fasRows.map((row, idx) => (
                                            <tr key={`${row.carrierID}-${row.mediaIp}-${idx}`}>
                                                <td style={td}><span className="badge bg-blue fs-12">{row.carrierName || row.carrierID}</span></td>
                                                <td style={td}><code style={{ color: '#334155', background: '#F1F5F9', padding: '2px 6px', borderRadius: '4px' }}>{row.mediaIp}</code></td>
                                                <td style={{ ...td, minWidth: '220px' }}>
                                                    <div style={{ fontWeight: '600', color: row.mediaOwner ? '#334155' : '#94A3B8' }}>
                                                        {row.mediaOwner || (row.mediaEnrichmentStatus === 'stale' ? 'Owner stale' : 'Owner pending')}
                                                    </div>
                                                    <div style={{ color: '#94A3B8', fontSize: '11px', lineHeight: '14px' }}>
                                                        {[row.mediaNetName, row.mediaCidr, row.mediaCountry].filter(Boolean).join(' · ')}
                                                        {row.mediaEnrichmentStatus === 'stale' ? ' · waiting for hourly refresh' : ''}
                                                    </div>
                                                </td>
                                                <td style={tdRight}>{fmtInt(row.attemptsCarrier)}</td>
                                                <td style={tdRight}>{fmtInt(row.completed)}</td>
                                                <td style={{ ...tdRight, fontWeight: '600', color: row.ASR >= 35 ? '#059669' : row.ASR >= 20 ? '#D97706' : '#DC2626' }}>{Number(row.ASR || 0).toFixed(2)}%</td>
                                                <td style={tdRight}>{Number(row.ACD || 0).toFixed(2)}</td>
                                                <td style={{ ...tdRight, fontWeight: '600', color: this.fasRiskScore(row) >= 45 ? '#DC2626' : this.fasRiskScore(row) >= 30 ? '#D97706' : '#64748B' }}>{this.fasRiskScore(row).toFixed(2)}</td>
                                                <td style={td}>
                                                    <span className={`badge ${this.fasRiskScore(row) >= 45 ? 'badge-danger' : this.fasRiskScore(row) >= 30 ? 'badge-warning' : 'badge-secondary'}`} style={{ fontSize: '11px' }}>
                                                        {this.fasReason(row)}
                                                    </span>
                                                </td>
                                                <td style={tdRight}>{fmtMins(row.minutes || 0)}</td>
                                                <td style={td}>
                                                    {blockedMediaIPs[row.mediaIp] ? (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                            <span className="badge badge-danger" style={{ fontSize: '11px' }}>Blocked</span>
                                                            <button type="button" className="btn btn-outline-secondary btn-sm" disabled={!!blockingMediaIP} onClick={() => this.unblockMediaIP(row)}>
                                                                {blockingMediaIP === row.mediaIp ? <i className="icon-spinner4 spinner"></i> : 'Unblock'}
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <button type="button" className="btn btn-outline-danger btn-sm" disabled={!!blockingMediaIP} onClick={() => this.blockMediaIP(row)}>
                                                            {blockingMediaIP === row.mediaIp ? <i className="icon-spinner4 spinner"></i> : 'Block IP'}
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {privateRows.length > 0 && (
                            <div className="table-responsive" style={{ maxHeight: '360px', overflowY: 'auto', marginTop: '18px', borderTop: '1px solid #E2E8F0', paddingTop: '14px' }}>
                                <div style={{ color: '#991B1B', fontWeight: '700', fontSize: '14px', marginBottom: '8px' }}>Private / Non-Public Media IPs</div>
                                <table className="table table-striped table-sm">
                                    <thead>
                                        <tr>
                                            <th style={th}>Carrier Name</th>
                                            <th style={th}>Media IP</th>
                                            <th style={th}>Scope</th>
                                            <th style={thRight}>Attempts</th>
                                            <th style={thRight}>Completed</th>
                                            <th style={thRight}>ASR</th>
                                            <th style={thRight}>ACD</th>
                                            <th style={th}>Signal</th>
                                            <th style={th}>Currently Blocked</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {privateRows.map((row, idx) => (
                                            <tr key={`private-${row.carrierID}-${row.mediaIp}-${idx}`}>
                                                <td style={td}><span className="badge bg-blue fs-12">{row.carrierName || row.carrierID}</span></td>
                                                <td style={td}><code style={{ color: '#991B1B', background: '#FEE2E2', padding: '2px 6px', borderRadius: '4px' }}>{row.mediaIp}</code></td>
                                                <td style={td}>{row.mediaIpScope || 'non-public'}</td>
                                                <td style={tdRight}>{fmtInt(row.attemptsCarrier)}</td>
                                                <td style={tdRight}>{fmtInt(row.completed)}</td>
                                                <td style={tdRight}>{Number(row.ASR || 0).toFixed(2)}%</td>
                                                <td style={tdRight}>{Number(row.ACD || 0).toFixed(2)}</td>
                                                <td style={td}>
                                                    <span className="badge badge-danger" style={{ fontSize: '11px' }}>
                                                        {this.fasReason(row)}
                                                    </span>
                                                </td>
                                                <td style={td}>
                                                    {blockedMediaIPs[row.mediaIp] ? <span className="badge badge-danger" style={{ fontSize: '11px' }}>Blocked</span> : <span className="badge badge-secondary" style={{ fontSize: '11px' }}>Internal IP</span>}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </Container>
        );
    }
}

export default CarrierMediaStatsReport;
